import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, CreditCard, SlidersHorizontal, Users2, Receipt, LogOut, Loader2, HeartCrack, LifeBuoy, Home } from "lucide-react";
import api from "@/lib/api";
import Metrics from "./Metrics";
import Subscriptions from "./Subscriptions";
import SaaSConfig from "./SaaSConfig";
import Users from "./Users";
import Households from "./Households";
import Payments from "./Payments";
import Churn from "./Churn";
import Tickets from "./Tickets";

const TABS = [
  { id: "metrics", label: "Overzicht", icon: LayoutDashboard },
  { id: "subs", label: "Abonnementen", icon: CreditCard },
  { id: "households", label: "Huishoudens", icon: Home },
  { id: "users", label: "Gebruikers", icon: Users2 },
  { id: "payments", label: "Betalingen", icon: Receipt },
  { id: "tickets", label: "Tickets", icon: LifeBuoy },
  { id: "churn", label: "Feedback", icon: HeartCrack },
  { id: "config", label: "SaaS-config", icon: SlidersHorizontal },
];

export default function SuperAdminPortal() {
  const navigate = useNavigate();
  const [me, setMe] = useState(undefined);
  const [tab, setTab] = useState("metrics");
  const [openTickets, setOpenTickets] = useState(0);

  const loadTicketCount = () => api.get("/admin/tickets/summary").then((r) => setOpenTickets(r.data.open || 0)).catch(() => {});

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { if (r.data?.role === "super_admin") setMe(r.data); else setMe(null); })
      .catch(() => setMe(null));
  }, []);

  useEffect(() => {
    if (!me) return;
    loadTicketCount();
    const iv = setInterval(loadTicketCount, 60000);
    return () => clearInterval(iv);
  }, [me]);

  useEffect(() => { if (me && tab !== "tickets") loadTicketCount(); }, [tab]);

  useEffect(() => { if (me === null) navigate("/superadmin/login", { replace: true }); }, [me, navigate]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("doenies_token");
    navigate("/superadmin/login", { replace: true });
  };

  if (me === undefined) return <div className="min-h-screen bg-slate-50 grid place-items-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!me) return null;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col md:flex-row">
      <aside className="md:w-64 md:min-h-screen bg-white border-b md:border-b-0 md:border-r border-slate-200 flex md:flex-col">
        <div className="p-5 flex items-center gap-3 md:border-b border-slate-200 flex-1 md:flex-none">
          <img src="/icon-192.png" alt="Slackr" className="w-10 h-10 rounded-xl shrink-0" />
          <div className="min-w-0">
            <p className="font-head font-black leading-none">Slackr</p>
            <p className="text-[11px] text-indigo-600 tracking-widest uppercase mt-1">Super Admin</p>
          </div>
        </div>
        <nav className="hidden md:flex flex-col gap-1 p-3 flex-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`sa-nav-${t.id}`}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-bold transition-colors ${tab === t.id ? "bg-indigo-50 text-indigo-700 border border-indigo-200" : "text-slate-500 hover:bg-slate-100"}`}>
              <t.icon className="w-4 h-4" strokeWidth={2.5} /> {t.label}
              {t.id === "tickets" && openTickets > 0 && (
                <span data-testid="sa-tickets-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-black grid place-items-center">{openTickets}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="p-3 md:border-t border-slate-200">
          <button onClick={logout} data-testid="sa-logout"
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-rose-600 px-3 py-2 rounded-lg hover:bg-slate-100 w-full">
            <LogOut className="w-4 h-4" /> <span className="hidden md:inline">Uitloggen</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <nav className="md:hidden flex overflow-x-auto gap-1 p-3 border-b border-slate-200 bg-white">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`sa-navm-${t.id}`}
              className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 ${tab === t.id ? "bg-indigo-50 text-indigo-700" : "text-slate-500"}`}>
              {t.label}
              {t.id === "tickets" && openTickets > 0 && (
                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-black grid place-items-center">{openTickets}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="p-5 md:p-8 max-w-6xl">
          <h1 className="font-head font-black text-2xl mb-6">{TABS.find((t) => t.id === tab)?.label}</h1>
          {tab === "metrics" && <Metrics />}
          {tab === "subs" && <Subscriptions />}
          {tab === "households" && <Households />}
          {tab === "users" && <Users meId={me.user_id} />}
          {tab === "payments" && <Payments />}
          {tab === "tickets" && <Tickets />}
          {tab === "churn" && <Churn />}
          {tab === "config" && <SaaSConfig />}
        </div>
      </main>
    </div>
  );
}
